import axios from "axios";
import { isPreOrderFlow } from "../../constants";

import { API_URL, ASSESSES_AFTER, BROKER_REFERRAL_SESSION_STORAGE_KEY } from "../../constants";
import {
  GET_ADDRESS_SUGGESTIONS,
  GET_ADDRESS_SUGGESTIONS_SUCCESS,
  GET_ADDRESS_SUGGESTIONS_FAIL,
  SET_ADDRESS,
  SET_USER_EMAIL,
  SET_ADDRESS_MODIFICATION,
  GET_PROPERTY_BY_ORDER,
  GET_PROPERTY_BY_ORDER_SUCCESS,
  GET_PROPERTY_BY_ORDER_FAIL,
  POST_ASSESSMENT_DATA_SUCCESS,
  POST_ASSESSMENT_DATA_FAIL,
  GET_PROPERTY_CURRENT_ASSESSMENT_FAIL,
  GET_PROPERTY_CURRENT_ASSESSMENT_SUCCESS,
  GET_PROPERTY_CURRENT_ASSESSMENT,
  POST_ASSESSMENT_DATA,
  GET_BROKER_BY_REFERRAL,
  GET_BROKER_BY_REFERRAL_SUCCESS,
  GET_BROKER_BY_REFERRAL_FAIL,
} from "../actionTypes";
import { showNotification } from "./notification";

// US state abbreviations EXCEPT Texas — used to detect when a typed address
// clearly targets another state, so the DB's loose token matches (e.g.
// "corey" → Houston "Corey Woods Ct") shouldn't be shown for "...mendham nj".
// CT (Court) and LA (Lane) are intentionally omitted: they collide with
// common street-suffix abbreviations and would false-positive a Texas
// address.  Those rarer cases fall through to the manual escape-hatch row.
const NON_TX_STATE_ABBRS = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "DE", "DC", "FL", "GA", "HI", "IA", "ID",
  "IL", "IN", "KS", "KY", "MA", "MD", "ME", "MI", "MN", "MO", "MS", "MT", "NC",
  "ND", "NE", "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "UT", "VA", "VT", "WA", "WI", "WV", "WY",
]);

// Texas ZIP prefixes (75xxx–79xxx, plus the 733xx Austin + 885xx El Paso
// ranges). A trailing 5-digit ZIP outside these is an out-of-state signal.
const isTxZip = (z) => /^(7[5-9]\d{3}|733\d\d|885\d\d)$/.test(z);

// True when the query looks like it's for an address OUTSIDE Texas — a
// non-TX state abbreviation as the (effective) last word, or a trailing
// non-Texas ZIP. Used to skip the noisy DB token-match and go straight to
// the external geocoder.
export const looksOutOfTexas = (query) => {
  const tokens = (query || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return false;
  const last = tokens[tokens.length - 1];
  const lastIsZip = /^\d{5}$/.test(last);
  // The token most likely to be the state: the last word, or the word
  // before a trailing ZIP ("... mendham nj 07945").
  const stateRaw = lastIsZip ? tokens[tokens.length - 2] || "" : last;
  const stateTok = stateRaw.replace(/[^a-z]/g, "").toUpperCase();
  if (stateTok.length === 2 && NON_TX_STATE_ABBRS.has(stateTok)) return true;
  if (lastIsZip && !isTxZip(last)) return true;
  return false;
};

// Monotonic token so only the most-recent lookup's results reach the
// store.  The fallback adds a second async hop (a slow geocoder can take
// seconds), so without this an older "Austin" response could land after a
// newer "Dallas" one and overwrite what the user is actually looking at.
// Guards the DB path too, which had the same latent race.
let _suggestSeq = 0;

// Photon (every-state) geocoder lookup, dispatched as the live suggestions.
// Results carry ``covered: false`` so selecting one routes to /coverage.
// Shares the sequence guard so a stale response can't clobber a newer one.
const fetchExternal = (dispatch, addressStr, seq, isStale) =>
  axios
    .get(`${API_URL}/properties/address-fallback/`, { params: { q: addressStr } })
    .then((fb) => {
      if (isStale()) return;
      const items = fb.status === 200 && Array.isArray(fb.data) ? fb.data : [];
      dispatch(getAddressSuggestionsSuccess(items));
    })
    .catch(() => {
      if (!isStale()) dispatch(getAddressSuggestionsSuccess([]));
    });

export const getAddressSuggestions = (addressStr) => {
  return (dispatch) => {
    const seq = ++_suggestSeq;
    const isStale = () => seq !== _suggestSeq;
    dispatch(getAddressSuggestionsStart());
    axios
      .post(`${API_URL}/properties/home_lookup_ajax/`, {
        q: addressStr,
        assessed_after: ASSESSES_AFTER,
      })
      .then((res) => {
        if (isStale()) return undefined; // a newer query superseded this one
        const covered = res.status === 200 && Array.isArray(res.data) ? res.data : [];
        // Show our DB matches UNLESS the query clearly targets another state
        // — then the loose token matches are noise, so go straight to the
        // external geocoder.  Otherwise, when the DB has nothing, fall back
        // too.  Uncovered (covered:false) picks route to /coverage.
        if (covered.length > 0 && !looksOutOfTexas(addressStr)) {
          dispatch(getAddressSuggestionsSuccess(covered));
          return undefined;
        }
        return fetchExternal(dispatch, addressStr, seq, isStale);
      })
      .catch((err) => {
        if (isStale()) return;
        dispatch(getAddressSuggestionsFail(err));
      });
  };
};

// Explicit "search every state" lookup — fired by the autosuggest escape
// hatch when the user doesn't see their address among the DB matches.
// Replaces the live suggestions with geocoder (covered:false) results.
export const getExternalAddressSuggestions = (addressStr) => {
  return (dispatch) => {
    const seq = ++_suggestSeq;
    const isStale = () => seq !== _suggestSeq;
    dispatch(getAddressSuggestionsStart());
    return fetchExternal(dispatch, addressStr, seq, isStale);
  };
};

export const getAddressSuggestionsStart = () => ({
  type: GET_ADDRESS_SUGGESTIONS,
});

export const getAddressSuggestionsSuccess = (items) => ({
  type: GET_ADDRESS_SUGGESTIONS_SUCCESS,
  payload: {
    items,
  },
});

export const getAddressSuggestionsFail = (error) => ({
  type: GET_ADDRESS_SUGGESTIONS_FAIL,
  payload: {
    error,
  },
});

export const setAddress = (address) => ({
  type: SET_ADDRESS,
  payload: {
    address,
  },
});

export const setUserEmail = (email) => ({
  type: SET_USER_EMAIL,
  payload: {
    email,
  },
});

export const setAssessmentFormValues = (values) => ({
  type: SET_ADDRESS_MODIFICATION,
  payload: {
    values,
  },
});

export const postAssessmentData = (discountObject, isFree, isPreOrder) => {
  // Returns the underlying axios promise so callers can chain on
  // it (e.g., navigate to ``/property-dossier/{uuid}`` once the
  // order is created).  Resolves to the response on success;
  // resolves to undefined on failure (errors are dispatched and
  // surfaced via notifications, callers usually want to stay put).
  return (dispatch, getState) => {
    dispatch(postAssessmentDataRequest());
    const { freeAssessment } = getState();
    const { selectedAddress, customerEmail, freeAssessmentFormValues, uuid } = freeAssessment;

    const referral = sessionStorage.getItem(BROKER_REFERRAL_SESSION_STORAGE_KEY);
    const changedValues = {};
    Object.keys(freeAssessmentFormValues).forEach((k) => {
      if (freeAssessmentFormValues[k] !== selectedAddress[k]) {
        changedValues[k] = freeAssessmentFormValues[k];
      }
    });

    const payload = {
      ...changedValues,
      email_address: customerEmail,
      is_primary_residence: !!freeAssessmentFormValues.is_primary_residence,
    };

    if (discountObject) {
      payload.discount_code = discountObject.value;
    }

    if (uuid) {
      payload.order_uuid = uuid;
    }

    if (!isPreOrder && !payload.discount_code) {
      payload.broker = referral;
    }

    const url = `${selectedAddress.url}begin-computation/`;
    return axios
      .post(url, payload)
      .then((res) => {
        if (res.data.uuid && referral && isPreOrderFlow) {
          axios.post(`${API_URL}/orders/${res.data.uuid}/attach-broker/`, {
            broker: referral,
          }).catch(() => {});
        }

        // Push to GTM dataLayer so GA4 / Meta Pixel / Google Ads can
        // measure the conversion.  Lives at the redux thunk
        // boundary because every entry point (LandingPage,
        // FreeAssessmentPage, PreOrderEstimate) flows through here,
        // so we get one event per real submission rather than one
        // per UI surface.
        require("../../utils/analytics").trackFreeAssessmentSubmitted({
          county: selectedAddress && selectedAddress.county,
          isPreOrder: Boolean(isPreOrder),
          hasReferral: Boolean(referral),
        });

        // Backend lets the order through at full price even when the
        // submitted code can't apply (stale link, code maxed out,
        // self-referral, repeat customer).  Surface the reason as a
        // friendly notification so the customer isn't confused why
        // their discount didn't appear, and clear the stored
        // referral so it doesn't keep auto-applying on subsequent
        // page loads.
        if (res.data && res.data.discount_skipped_reason) {
          const reason = res.data.discount_skipped_reason;
          const codeShown = res.data.discount_skipped_code || "your code";
          const messages = {
            self_referral: `That referral code (${codeShown}) is your own — discounts can't apply to your own purchase. Proceeding at regular price.`,
            repeat_customer: `Looks like you've already used a referral discount before — referrals are for first-time orders only. Proceeding at regular price.`,
            code_not_found: `We couldn't find the discount code "${codeShown}". Proceeding at regular price.`,
            code_inactive: `The discount code "${codeShown}" isn't active anymore. Proceeding at regular price.`,
          };
          dispatch(showNotification(
            messages[reason] || `We couldn't apply your discount code. Proceeding at regular price.`
          ));
          // Clear so the same stale slug doesn't auto-apply on the
          // next flow.  Best-effort — falls back to a no-op when
          // localStorage isn't available.
          try {
            require("../../utils/referralCode").clearStoredReferral();
          } catch (_e) { /* ignore */ }
        }

        dispatch(postAssessmentDataSuccess({ response: res, isPreOrder: isPreOrder }));
        // Resolve to the response so callers can navigate to
        // /property-dossier/{uuid} once the order is created.
        return res;
      })
      .catch((err) => {
        dispatch(postAssessmentDataError(err));
        if (err.response && err.response.status === 409) {
          const code = err.response.data?.code;
          if (code === "duplicate_unpaid") {
            dispatch(showNotification(
              "Thank you. We already have a request for this property. " +
              "Please check your email, including spam, for your estimate or checkout link."
            ));
          } else if (code === "duplicate_paid" || code === "duplicate_paid_other_email") {
            dispatch(showNotification(
              "Thank you. We have an existing order on file for this address for the year. " +
              "Please email help@protestingpropertytaxes.com for more information."
            ));
          } else {
            dispatch(showNotification(
              "You already have an order for this property this year. Check your email for order details."
            ));
          }
        } else {
          dispatch(showNotification("Something went wrong submitting your assessment. Please try again."));
        }
      });
  };
};

export const postAssessmentDataRequest = () => ({
  type: POST_ASSESSMENT_DATA,
});

export const postAssessmentDataSuccess = (payload) => ({
  type: POST_ASSESSMENT_DATA_SUCCESS,
  payload,
});

export const postAssessmentDataError = (err) => ({
  type: POST_ASSESSMENT_DATA_FAIL,
  payload: {
    error: err,
  },
});

export const getDiscountCode = (code) => axios.post(`${API_URL}/discount-codes/validate/`, { discount_code: code });

export const postDiscountCodeToOrder = (code, orderId) =>
  axios.post(`${API_URL}/orders/${orderId}/apply-discount/`, {
    discount_code: code,
  });

export const getPropertyByOrder = (orderNumber, handleError) => (dispatch) => {
  dispatch(getPropertyByOrderStart());
  axios
    .get(`${API_URL}/orders/${orderNumber}/confirm/`)
    .then((res) => {
      if (res.status === 200) {
        const { data } = res;
        const { pxn_prop_id, email_address, amount, broker, discount_code } = data;
        axios.get(`${API_URL}/properties/${pxn_prop_id}/`).then((res) => {
          if (res.status === 200) {
            const { data } = res;
            dispatch(getPropertyByOrderSuccess(data, email_address || "", orderNumber, amount, broker, discount_code));
          } else {
            throw new Error("No property");
          }
        }).catch((err) => {
          handleError && handleError();
          dispatch(getPropertyByOrderError(err));
        });
      }
    })
    .catch((err) => {
      handleError && handleError();
      dispatch(getPropertyByOrderError(err));
      dispatch(showNotification("Unable to load your order. Please check your link and try again."));
    });
};

export const getPropertyById = (propertyId, handleError) => (dispatch, getState) => {
  const { freeAssessment } = getState();

  dispatch(getPropertyByOrderStart());
  axios
    .get(`${API_URL}/properties/${propertyId}`)
    .then((res) => {
      if (res.status === 200) {
        dispatch(getPropertyByOrderSuccess(res.data, freeAssessment.customerEmail || "", null, null, null));
      }
    })
    .catch((err) => {
      handleError && handleError();
      dispatch(getPropertyByOrderError(err));
      dispatch(showNotification("Unable to load property information. Please try again."));
    });
};

export const getPropertyByOrderStart = () => ({
  type: GET_PROPERTY_BY_ORDER,
});

export const getPropertyByOrderSuccess = (property, email, uuid, price, broker, discountCode) => ({
  type: GET_PROPERTY_BY_ORDER_SUCCESS,
  payload: {
    property,
    email,
    uuid,
    price,
    broker,
    discountCode,
  },
});

export const getPropertyByOrderError = (err) => ({
  type: GET_PROPERTY_BY_ORDER_FAIL,
  payload: {
    error: err,
  },
});

export const getPropertyCurrentAssessment = () => (dispatch, getState) => {
  const { freeAssessment } = getState();
  const { selectedAddress } = freeAssessment;

  dispatch({ type: GET_PROPERTY_CURRENT_ASSESSMENT });

  axios
    .get(`${selectedAddress.url}current_assessment/`)
    .then((res) => {
      if (res.status === 200) {
        dispatch(getPropertyCurrentAssessmentSuccess(res.data));
      }
    })
    .catch((err) => {
      dispatch(getPropertyCurrentAssessmentFail());
    });
};

export const getPropertyCurrentAssessmentFail = () => ({
  type: GET_PROPERTY_CURRENT_ASSESSMENT_FAIL,
});

export const getPropertyCurrentAssessmentSuccess = (payload) => ({
  type: GET_PROPERTY_CURRENT_ASSESSMENT_SUCCESS,
  payload,
});

export const getBrokerByReferral = (referral) => (dispatch) => {
  dispatch({ type: GET_BROKER_BY_REFERRAL });

  axios
    .get(`${API_URL}/brokers/${referral}`)
    .then((res) => {
      if (res.status === 200) {
        dispatch(getBrokerByReferralSuccess(res.data));
      }
    })
    .catch((err) => {
      dispatch(getBrokerByReferralFail(err));
    });
};

export const getBrokerByReferralFail = (error) => ({
  type: GET_BROKER_BY_REFERRAL_FAIL,
  payload: {
    error,
  },
});

export const getBrokerByReferralSuccess = (payload) => ({
  type: GET_BROKER_BY_REFERRAL_SUCCESS,
  payload,
});
